import React, { forwardRef } from "react";

import classNames from "classnames";

import MenuButton from "./MenuButton";
import { collapsedSidebarClassNames, useSidebarContext } from "./Sidebar";
import AbilityContainer from "../utils/abilities/AbilityContainer";

import type { JSX, Ref } from "react";

import type { AbilityKey } from "../utils/abilities/AbilityContainer";

interface MenuItemProps {
  label: string;
  icon?: JSX.Element;
  suffix?: JSX.Element;
  className?: string;
  isInSubmenu?: boolean;
  isSubmenuOpen?: boolean;
  component?: React.ReactElement | string;
  abilityList?: AbilityKey[] | undefined;
}

function MenuItem(
  {
    label,
    icon,
    suffix,
    component,
    className,
    isInSubmenu,
    isSubmenuOpen,
    abilityList,
  }: MenuItemProps,
  ref: Ref<HTMLDivElement>,
): JSX.Element {
  const { isCollapsed } = useSidebarContext();

  const menuItemClassNames = classNames(
    "flex h-12.5 items-center px-[1.09375rem] relative text-muted-foreground hover:bg-surface-muted gap-[0.78125rem] cursor-pointer",
    "before:hidden before:absolute before:top-0 before:left-0 before:w-1.25 before:bottom-0 before:bg-primary",
    "group-[.active]:before:block group-[.active]:text-primary group-[.active]:font-semibold", // Active NavLink state
    isSubmenuOpen && isCollapsed && "bg-muted",
    className,
  );

  const labelClassNames = classNames(
    "transition-opacity ease-in-out",
    collapsedSidebarClassNames(isCollapsed && !isInSubmenu),
  );

  return (
    <AbilityContainer abilityList={abilityList}>
      <MenuButton component={component}>
        <div
          ref={ref}
          className={menuItemClassNames}
          data-tooltip-content={label}
          data-tooltip-hidden={(!isCollapsed || isInSubmenu) ?? isSubmenuOpen}
          data-tooltip-id="menu-tooltip"
        >
          {icon ? <div>{icon}</div> : null}
          <span className={labelClassNames}>{label}</span>
          {suffix ? <div className="ml-auto">{suffix}</div> : null}
        </div>
      </MenuButton>
    </AbilityContainer>
  );
}

export default forwardRef(MenuItem);
